import type { Flavor } from "./Util";

export namespace Proto {
    // Types of boolean values
    export type NumericalBoolean = 0 | 1;
    export type CharBoolean = "T" | "F";
    export type LowerCharBoolean = "t" | "f";
    export type LowerStringBoolean = "true" | "false";

    // String flavors
    export type NumericalString = Flavor<string, number>;
    export type XMLString = Flavor<string, "XML">;
    export type HTMLString = Flavor<string, "HTML">;
    export type URLString = Flavor<string, "URL">;
    /* e.g. 2025-12-31 with optional T23:59:59 */
    export type ISODateString = Flavor<string, "ISO 8601">;
    export type ISODurationString = Flavor<string, "ISO 8601 Duration">;
    export type OffsetISODateString = Flavor<ISODateString, "offset">;
    export type ISOTimeString = Flavor<string, "12:34:56">;
    export type EmailString = Flavor<string, "email">;
    export type PhoneString = Flavor<string, "555-555-5555">;
    export type QLString = Flavor<string, "ql">;
    export type Char = Flavor<string, "char">;
    export type AlphaString = Flavor<string, "alpha">;
    export type AlphaNumericalString = AlphaString | NumericalString;
    export type AlphaNumericalChar = Flavor<AlphaNumericalString, "char">;
    export type CRC = Flavor<string, "CRC">;
    export type HexColor = Flavor<string, "#012345AA">;
    export type PercentageString = Flavor<string, "\\d+%">;
    export type EventReference = Flavor<string, "1234-ABCDEF">;
    export type DateFormat = Flavor<string, "date format">;
    export type TimeFormat = Flavor<string, "time format">;

    // Number types
    export type Fraction = Flavor<number, "fraction">; // Typically between 0 and 1
    export type DowNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 6 = Saturday
    export type EpochTimestamp = Flavor<number, "epoch">; // Unix epoch
    export type Integer = Flavor<number, "integer">; // Only integers
    export type Milliseconds = Flavor<number, "milliseconds">;

    // Service types
    export type ServiceStatus = "est" | "mod" | "del" | "new";
    export type ServiceMeta<Engine extends string = "accl"> = { engine: Engine; pubdate: OffsetISODateString };
    // Plus-separated (ANY) or comma-separated (ALL) list of values
    export type WsMultiParam = WsMultiParamAny | WsMultiParamAll;
    export type WsMultiParamAny = Flavor<string, "+ separated">;
    export type WsMultiParamAll = Flavor<string, ", separated">;

    // Other
    export type Primitive = string | Function | number | boolean | symbol | undefined | null;
    export type TODO = never; // Temporary type until we know what type it really is
    export type Result<Ok, Err> = [Ok, Err];
    export type ResultObj<Data, Err = any> = { data?: Data; error?: Err };
    export type Option<Some> = { data?: Some; none?: true };
    export type Unwrap<R extends Result<unknown, unknown> | ResultObj<unknown, unknown>> =
        R extends Result<unknown, unknown> ? R[0] : R extends ResultObj<unknown, unknown> ? R["data"] : never;
    export type DowName = "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday";
    export const DowName = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
    ] as const satisfies DowName[];
    export type DowNameLowercase = Lowercase<DowName>;
    export const DowNameLowercase = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
    ] as const satisfies DowNameLowercase[];
    /** Series / profile weekly DOW tokens; index aligns with DowNumber (0 = Sunday). */
    export type DowAbbr = "SU" | "MO" | "TU" | "WE" | "TH" | "FR" | "SA";
    export const DowAbbr = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const satisfies DowAbbr[];
    export type DowShortLabel = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";
    export const DowShortLabel = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const satisfies DowShortLabel[];

    // Event link format types
    export type EventLinkFormat = "pro" | "25live" | "scheduling" | "noLink";

    export type Paginate = {
        page_num: number;
        page_size: number;
        total_results: number;
        page_count: number;
        paginate_key: string;
    };
}
